/**
 * Analytics Dashboard Component
 * Displays key validation metrics to understand product-market fit.
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  EmojiEvents as TrophyIcon,
  Favorite as HeartIcon,
  Speed as SpeedIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Timeline as TimelineIcon,
  Share as ShareIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { analyticsService, UserAnalyticsSummary } from '../services/analyticsService';

interface AnalyticsDashboardProps {
  userId: string;
  userLanguage?: 'zh-CN' | 'es' | 'fr' | 'en';
}

const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ userId, userLanguage = 'en' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [summary, setSummary] = useState<UserAnalyticsSummary | null>(null);
  const [ahaMomentScore, setAhaMomentScore] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getText = (key: string): string => {
    const texts: Record<string, Record<string, string>> = {
      'zh-CN': {
        title: '你的进度仪表板',
        engagement: '参与度指标',
        retention: '留存指标',
        behavior: '行为改变',
        emotional: '情感连接',
        ahaScore: '啊哈时刻得分',
        totalMessages: '总消息数',
        conversations: '对话数',
        roadmapsGenerated: '生成的训练计划',
        roadmapsCompleted: '完成的训练计划',
        completionRate: '完成率',
        returnVisits: '回访次数',
        currentStreak: '当前连续天数',
        longestStreak: '最长连续记录',
        dailyCheckins: '每日签到',
        activitiesCompleted: '完成的活动',
        petMentions: '提到宠物名字',
        hasReachedAha: '已达到啊哈时刻',
        notYetReached: '尚未达到',
        loading: '加载中...',
        error: '加载分析数据时出错',
        congratulations: '恭喜！你正在成为理想的宠物主人',
        keepGoing: '继续保持！',
        scoreDescription: '此得分衡量你从尝试到相信的转变'
      },
      'es': {
        title: 'Tu Panel de Progreso',
        engagement: 'Métricas de Compromiso',
        retention: 'Métricas de Retención',
        behavior: 'Cambio de Comportamiento',
        emotional: 'Conexión Emocional',
        ahaScore: 'Puntuación de Momento Aha',
        totalMessages: 'Total de Mensajes',
        conversations: 'Conversaciones',
        roadmapsGenerated: 'Planes de Entrenamiento Generados',
        roadmapsCompleted: 'Planes de Entrenamiento Completados',
        completionRate: 'Tasa de Finalización',
        returnVisits: 'Visitas de Retorno',
        currentStreak: 'Racha Actual',
        longestStreak: 'Racha Más Larga',
        dailyCheckins: 'Check-ins Diarios',
        activitiesCompleted: 'Actividades Completadas',
        petMentions: 'Menciones de Nombre de Mascota',
        hasReachedAha: 'Ha Alcanzado el Momento Aha',
        notYetReached: 'Aún No Alcanzado',
        loading: 'Cargando...',
        error: 'Error al cargar datos de análisis',
        congratulations: '¡Felicitaciones! Te estás convirtiendo en el dueño de mascota ideal',
        keepGoing: '¡Sigue así!',
        scoreDescription: 'Esta puntuación mide tu transformación de intentar a creer'
      },
      'fr': {
        title: 'Votre Tableau de Progression',
        engagement: 'Métriques d\'Engagement',
        retention: 'Métriques de Rétention',
        behavior: 'Changement de Comportement',
        emotional: 'Connexion Émotionnelle',
        ahaScore: 'Score du Moment Aha',
        totalMessages: 'Total des Messages',
        conversations: 'Conversations',
        roadmapsGenerated: 'Plans d\'Entraînement Générés',
        roadmapsCompleted: 'Plans d\'Entraînement Terminés',
        completionRate: 'Taux de Complétion',
        returnVisits: 'Visites de Retour',
        currentStreak: 'Série Actuelle',
        longestStreak: 'Plus Longue Série',
        dailyCheckins: 'Enregistrements Quotidiens',
        activitiesCompleted: 'Activités Terminées',
        petMentions: 'Mentions du Nom de l\'Animal',
        hasReachedAha: 'A Atteint le Moment Aha',
        notYetReached: 'Pas Encore Atteint',
        loading: 'Chargement...',
        error: 'Erreur lors du chargement des données analytiques',
        congratulations: 'Félicitations ! Vous devenez le parent d\'animal idéal',
        keepGoing: 'Continuez comme ça !',
        scoreDescription: 'Ce score mesure votre transformation d\'essayer à croire'
      },
      'en': {
        title: 'Your Progress Dashboard',
        engagement: 'Engagement Metrics',
        retention: 'Retention Metrics',
        behavior: 'Behavior Change',
        emotional: 'Emotional Connection',
        ahaScore: 'Aha Moment Score',
        totalMessages: 'Total Messages',
        conversations: 'Conversations',
        roadmapsGenerated: 'Training Plans Generated',
        roadmapsCompleted: 'Training Plans Completed',
        completionRate: 'Completion Rate',
        returnVisits: 'Return Visits',
        currentStreak: 'Current Streak',
        longestStreak: 'Longest Streak',
        dailyCheckins: 'Daily Check-ins',
        activitiesCompleted: 'Activities Completed',
        petMentions: 'Pet Name Mentions',
        hasReachedAha: 'Has Reached Aha Moment',
        notYetReached: 'Not Yet Reached',
        loading: 'Loading...',
        error: 'Error loading analytics data',
        congratulations: 'Congratulations! You\'re becoming the ideal pet parent',
        keepGoing: 'Keep going!',
        scoreDescription: 'This score measures your transformation from trying to believing'
      }
    };

    return texts[userLanguage]?.[key] || texts['en'][key] || key;
  };

  useEffect(() => {
    loadAnalytics();
  }, [userId]);

  const loadAnalytics = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const userSummary = await analyticsService.getUserSummary(userId);
      const ahaScore = await analyticsService.calculateAhaMomentScore(userId);

      setSummary(userSummary);
      setAhaMomentScore(ahaScore);
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError(getText('error'));
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          {getText('loading')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!summary) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No analytics data available yet. Start using PetSnap to see your progress!</Alert>
      </Box>
    );
  }

  const completionRate = summary.totalRoadmapsGenerated > 0
    ? (summary.totalRoadmapsCompleted / summary.totalRoadmapsGenerated) * 100
    : 0;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <TimelineIcon sx={{ mr: 1, color: 'primary.main' }} />
        {getText('title')}
      </Typography>

      {/* Aha Moment Score */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' } }}>
            <Box sx={{ mb: { xs: 2, sm: 0 } }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <TrophyIcon sx={{ mr: 1 }} />
                {getText('ahaScore')}
                <Tooltip title={getText('scoreDescription')}>
                  <IconButton size="small" sx={{ ml: 1, color: 'white' }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Typography>
              <Typography variant="h2" sx={{ fontWeight: 'bold' }}>
                {ahaMomentScore}/100
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                {ahaMomentScore >= 70
                  ? getText('congratulations')
                  : getText('keepGoing')
                }
              </Typography>
            </Box>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress
                variant="determinate"
                value={ahaMomentScore}
                size={100}
                thickness={4}
                sx={{ color: 'white' }}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <TrophyIcon sx={{ fontSize: 40, opacity: 0.5 }} />
              </Box>
            </Box>
          </Box>
          <LinearProgress
            variant="determinate"
            value={ahaMomentScore}
            sx={{
              mt: 2,
              height: 8,
              borderRadius: 4,
              backgroundColor: 'rgba(255,255,255,0.3)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'white'
              }
            }}
          />
        </CardContent>
      </Card>

      {/* Engagement Metrics */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, display: 'flex', alignItems: 'center' }}>
        <SpeedIcon sx={{ mr: 1, color: 'primary.main' }} />
        {getText('engagement')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' } }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {getText('totalMessages')}
              </Typography>
              <Typography variant="h4" color="primary">
                {summary.totalMessagesent.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' } }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {getText('conversations')}
              </Typography>
              <Typography variant="h4" color="primary">
                {summary.totalConversations}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' } }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {getText('roadmapsGenerated')}
              </Typography>
              <Typography variant="h4" color="primary">
                {summary.totalRoadmapsGenerated}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' } }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {getText('roadmapsCompleted')}
              </Typography>
              <Typography variant="h4" color="primary">
                {summary.totalRoadmapsCompleted}
              </Typography>
              {summary.totalRoadmapsGenerated > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {getText('completionRate')}: {completionRate.toFixed(0)}%
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Retention Metrics */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, display: 'flex', alignItems: 'center' }}>
        <PeopleIcon sx={{ mr: 1, color: 'success.main' }} />
        {getText('retention')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' } }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {getText('returnVisits')}
              </Typography>
              <Typography variant="h4" color="success.main">
                {summary.totalReturnVisits}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' } }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {getText('currentStreak')}
              </Typography>
              <Typography variant="h4" color="success.main">
                {summary.consecutiveDaysStreak} 🔥
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' } }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {getText('longestStreak')}
              </Typography>
              <Typography variant="h4" color="success.main">
                {summary.longestStreak} 🏆
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' } }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {getText('dailyCheckins')}
              </Typography>
              <Typography variant="h4" color="success.main">
                {summary.dailyCheckins}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Behavioral Change Metrics */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, display: 'flex', alignItems: 'center' }}>
        <CheckCircleIcon sx={{ mr: 1, color: 'info.main' }} />
        {getText('behavior')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {getText('activitiesCompleted')}
              </Typography>
              <Typography variant="h4" color="info.main">
                {summary.activitiesCompleted}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.min((summary.activitiesCompleted / 100) * 100, 100)}
                sx={{ mt: 2 }}
              />
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {getText('completionRate')}
              </Typography>
              <Typography variant="h4" color="info.main">
                {completionRate.toFixed(0)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={completionRate}
                sx={{ mt: 2 }}
                color="info"
              />
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Emotional Connection Metrics */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, display: 'flex', alignItems: 'center' }}>
        <HeartIcon sx={{ mr: 1, color: 'error.main' }} />
        {getText('emotional')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {getText('petMentions')}
              </Typography>
              <Typography variant="h4" color="error.main">
                {summary.petNameMentions} ❤️
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Shows emotional attachment to AI assistant
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {getText('hasReachedAha')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                {summary.hasReachedAhaMoment ? (
                  <>
                    <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mr: 2 }} />
                    <Typography variant="h6" color="success.main">
                      ✅ {getText('hasReachedAha')}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="h6" color="text.secondary">
                    ⏳ {getText('notYetReached')}
                  </Typography>
                )}
              </Box>
              {summary.ahaMomentDate && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Reached on: {summary.ahaMomentDate.toDate().toLocaleDateString()}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Referral Metrics */}
      {(summary.petProfilesShared > 0 || summary.referralLinksGenerated > 0) && (
        <>
          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, display: 'flex', alignItems: 'center' }}>
            <ShareIcon sx={{ mr: 1, color: 'warning.main' }} />
            Referral Activity
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Pet Profiles Shared
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {summary.petProfilesShared}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Referral Links Generated
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {summary.referralLinksGenerated}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default AnalyticsDashboard;

